import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';

import {
  type CardDisputeDocumentEvidenceInput,
  CardDisputeEligibilityReason,
  cardsGetCardTransactionOptions,
  cardsListCardDisputesOptions,
} from '@column/api';
import { Button, formatNumber } from '@column/column-ui-kit';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@column/ui';

import { DisputeEvidenceComposer } from '../Evidence/DisputeEvidenceComposer';
import { IssuerDashboardGate } from '../IssuerDashboardGate';
import { UnauthorizedState } from '~/app/_components';
import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { useDebounce } from '~/hooks/useDebounce';
import { useQueryParams } from '~/hooks/useQueryParams';
import {
  apiErrorDetail,
  apiErrorMessage,
  apiErrorType,
  buildCardDisputesListQuery,
  useCanManageCardDisputes,
  useCreateCardDispute,
} from '~/lib/api/cardDisputes';
import {
  CARD_DISPUTE_ELIGIBILITY_REASON_LABELS,
  CARD_DISPUTE_REASON_LABELS,
  type CardDisputeReason,
  getWorkflowLabel,
} from '~/lib/disputes/labels';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { DisputeReasonForm } from './DisputeReasonForm';
import {
  REASON_WORKFLOW,
  buildCreateDisputeRequest,
  buildEligibilityRequest,
  disputeSubmissionReadiness,
  emptyReasonFormState,
  exceedsReceiptDifference,
  findOversizedContactDetail,
  formatSmallestUnitToMajorUnits,
  isPartialClaim,
  missingReasonFields,
  parseMajorUnitsToSmallestUnit,
  questionnaireAnswers,
  reasonForPartialDisputeTooLong,
} from './questionnaire';
import {
  ACCOUNT_STATUS_LABELS,
  FRAUD_TYPE_LABELS,
  MERCHANDISE_OR_SERVICES_LABELS,
  MERCHANT_CONTACT_METHOD_LABELS,
  MERCHANT_RETURN_REFUSAL_REASON_LABELS,
} from './questionnaireLabels';
import { ReasonField } from './ReasonFields';
import { useCardDisputeEligibility } from './useCardDisputeEligibility';

type WizardStep = 'transaction' | 'claim' | 'evidence' | 'review';

interface NewDisputeQueryParams {
  transaction_id?: string;
}

const REASON_OPTIONS = (Object.keys(CARD_DISPUTE_REASON_LABELS) as CardDisputeReason[]).map((reason) => ({
  value: reason,
  label: CARD_DISPUTE_REASON_LABELS[reason],
}));

const ANSWER_LABELS = {
  fraudType: (value: string) => FRAUD_TYPE_LABELS[value as keyof typeof FRAUD_TYPE_LABELS] ?? value,
  accountStatus: (value: string) => ACCOUNT_STATUS_LABELS[value as keyof typeof ACCOUNT_STATUS_LABELS] ?? value,
  merchandiseOrServices: (value: string) =>
    MERCHANDISE_OR_SERVICES_LABELS[value as keyof typeof MERCHANDISE_OR_SERVICES_LABELS] ?? value,
  contactMethod: (value: string) =>
    MERCHANT_CONTACT_METHOD_LABELS[value as keyof typeof MERCHANT_CONTACT_METHOD_LABELS] ?? value,
  returnRefusalReason: (value: string) =>
    MERCHANT_RETURN_REFUSAL_REASON_LABELS[value as keyof typeof MERCHANT_RETURN_REFUSAL_REASON_LABELS] ?? value,
};

const workflowLabels = (value: string) => getWorkflowLabel(value);

export const PageCardDisputeNew: React.FC = () => {
  const canManage = useCanManageCardDisputes();
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);

  return (
    <IssuerDashboardGate>
      {/* Remounting on platform/mode change resets every in-memory answer. */}
      {canManage ? (
        <PageCardDisputeNewContent key={`${platformId ?? ''}:${isSandbox ? 'sandbox' : 'live'}`} />
      ) : (
        <UnauthorizedState />
      )}
    </IssuerDashboardGate>
  );
};

const PageCardDisputeNewContent: React.FC = () => {
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();
  const { queryParams } = useQueryParams<NewDisputeQueryParams>();

  const [step, setStep] = useState<WizardStep>(queryParams?.transaction_id ? 'claim' : 'transaction');
  const [transactionIdInput, setTransactionIdInput] = useState(queryParams?.transaction_id ?? '');
  const [transactionId, setTransactionId] = useState(queryParams?.transaction_id ?? '');
  const [lookupTransaction] = useDebounce((value: string) => setTransactionId(value), 300);
  const [reason, setReason] = useState<CardDisputeReason | ''>('');
  const [reasonState, setReasonState] = useState(emptyReasonFormState);
  const [claimAmount, setClaimAmount] = useState('');
  const [partialExplanation, setPartialExplanation] = useState('');
  const [narrative, setNarrative] = useState('');
  const [evidence, setEvidence] = useState<CardDisputeDocumentEvidenceInput[]>([]);
  const [wantsCredit, setWantsCredit] = useState(false);
  const [creditConfirmed, setCreditConfirmed] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>();

  const eligibility = useCardDisputeEligibility();
  const createMutation = useCreateCardDispute();

  const transactionQuery = useQuery({
    ...cardsGetCardTransactionOptions({ path: { card_transaction_id: transactionId } }),
    enabled: Boolean(transactionId),
    retry: false,
  });
  const transaction = transactionId === transactionIdInput.trim() ? transactionQuery.data : undefined;

  const activeDisputesQuery = useQuery({
    ...cardsListCardDisputesOptions({
      query: buildCardDisputesListQuery({ query: { card_transaction_id: transactionId || undefined }, pageSize: 25 }),
    }),
    enabled: Boolean(transactionId),
  });
  const activeDispute = activeDisputesQuery.data?.card_disputes.find(
    (entry) =>
      entry.card_transaction_id === transactionId && entry.stage !== 'dispute_won' && entry.stage !== 'dispute_close'
  );

  const settled = transaction?.total_amounts?.cardholder_settled;
  // Settled debits are stored negative; the claim amount is positive, in the billing currency
  // derived from the cardholder settled amount — never the merchant transaction currency.
  const settledSmallest = settled ? Math.abs(settled.amount) : undefined;
  const currency = settled?.currency_code ?? '';

  const claimSmallest = currency ? parseMajorUnitsToSmallestUnit(claimAmount, currency) : undefined;
  const claimPartial =
    claimSmallest !== undefined && settledSmallest !== undefined && isPartialClaim(claimSmallest, settledSmallest);
  const receiptAmount =
    reason === 'incorrect_amount'
      ? parseMajorUnitsToSmallestUnit(reasonState.incorrect_amount?.cardholder_receipt_amount, currency)
      : undefined;
  const claimOverReceiptDifference =
    reason === 'incorrect_amount' &&
    exceedsReceiptDifference({ claimAmount: claimSmallest ?? 0, settledAmount: settledSmallest ?? 0, receiptAmount });
  const missingFields = reason ? missingReasonFields(reason, reasonState, currency) : [];
  const oversizedField =
    reason === 'canceled_recurring_transaction' ? findOversizedContactDetail(reasonState) : undefined;

  const hasUnsavedWork =
    Boolean(reason) ||
    claimAmount !== '' ||
    partialExplanation !== '' ||
    narrative !== '' ||
    evidence.length > 0 ||
    wantsCredit;
  // Set once the draft exists so a retry after a saved-but-unnavigated create cannot double-create.
  const savedDisputeRef = useRef<string>();
  // The app uses a plain BrowserRouter (no data router), so useBlocker is unavailable — it throws
  // at render. The browser-native guard covers the data-loss-prone paths: closing or refreshing
  // the tab with answers still in memory.
  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (hasUnsavedWork && !savedDisputeRef.current) {
          event.preventDefault();
          event.returnValue = 'Leave with an unsaved dispute? Your answers will be lost.';
        }
      },
      [hasUnsavedWork]
    )
  );

  useEffect(() => {
    if (transactionQuery.error) {
      addDangerNotification({ content: 'Card transaction not found. Check the ID and try again.' });
      setStep('transaction');
    }
  }, [transactionQuery.error, addDangerNotification]);

  // Prefill the claim with the settled amount once per transaction, whichever entry path loaded it
  // (transaction lookup or the deep link from transaction detail). Never overwrites user input.
  const prefilledTransactionRef = useRef<string>();
  useEffect(() => {
    if (!transaction || !settledSmallest || !currency || prefilledTransactionRef.current === transaction.id) {
      return;
    }
    prefilledTransactionRef.current = transaction.id;
    setClaimAmount((current) => current || formatSmallestUnitToMajorUnits(settledSmallest, currency));
  }, [transaction, settledSmallest, currency]);

  const handleTransactionIdChange = (value: string) => {
    setTransactionIdInput(value);
    if (value.trim() === transactionIdInput.trim()) {
      return;
    }
    eligibility.reset();
    setShowValidation(false);
    setCreateError(undefined);
    setClaimAmount('');
    prefilledTransactionRef.current = undefined;
    setTransactionId('');
    lookupTransaction(value.trim());
  };

  const canContinueWithTransaction =
    Boolean(transaction && settledSmallest && currency) && transactionQuery.isSuccess && !transactionQuery.isFetching;

  const handleTransactionConfirmed = () => {
    if (!canContinueWithTransaction) {
      return;
    }
    setStep('claim');
  };

  const handleClaimContinue = async () => {
    if (
      !reason ||
      claimSmallest === undefined ||
      claimSmallest <= 0 ||
      claimOverReceiptDifference ||
      oversizedField ||
      missingFields.length > 0
    ) {
      setShowValidation(true);
      return;
    }

    const result = await eligibility.check(
      buildEligibilityRequest({
        cardTransactionId: transactionId,
        amount: claimSmallest,
        currency,
        reason,
        formState: reasonState,
        evidence: [],
      })
    );
    if (!result) {
      return;
    }
    // missing_required_evidence does not block: narrative and documents are collected next, and a
    // draft may be saved without them. Every other reported reason is a hard blocker.
    const blockers = result.reasons.filter((entry) => entry !== CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE);
    if (blockers.length > 0) {
      return;
    }
    setStep('evidence');
  };

  const handleReviewContinue = () => {
    if (!reason || missingFields.length > 0 || oversizedField) {
      setShowValidation(true);
      setStep('claim');
      return;
    }
    setStep('review');
  };

  const handleSaveDraft = async () => {
    if (!reason || claimSmallest === undefined || !currency || (wantsCredit && !creditConfirmed)) {
      return;
    }

    const request = buildCreateDisputeRequest({
      cardTransactionId: transactionId,
      claimAmount: claimSmallest,
      currency,
      reason,
      formState: reasonState,
      narrative,
      evidence,
      partial: claimPartial,
      partialExplanation,
      issueProvisionalCredit: wantsCredit,
    });

    setCreateError(undefined);
    try {
      const dispute = await createMutation.mutateAsync({ request, transactionId });
      savedDisputeRef.current = dispute.id;
      navigate(`${ROUTE.CARD_DISPUTES}/edit/${dispute.id}`);
    } catch (error) {
      if (apiErrorType(error) === 'transfer_non_sufficient_fund') {
        // Funding failures keep the form and the selected financial request untouched.
        setCreateError(
          'The program operating account does not have enough funds to issue the provisional credit. The dispute was not created.'
        );
        return;
      }
      // The server's already-disputed guard carries the winning dispute's ID: a concurrent create
      // (or a dispute that raced us) saved the record. Navigate to it instead of creating a
      // duplicate. Any other failure keeps the user on the form with the real error.
      const winningDisputeId = apiErrorDetail(error, 'card_dispute_id');
      if (winningDisputeId) {
        savedDisputeRef.current = winningDisputeId;
        navigate(`${ROUTE.CARD_DISPUTES}/edit/${winningDisputeId}`);
        return;
      }
      setCreateError(apiErrorMessage(error, 'The dispute was not saved. Check for errors and try again.'));
    }
  };

  const transactionSummary = transaction && settled && (
    <div
      className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-4"
      data-testid="new-dispute-transaction-summary"
    >
      <SummaryItem label="Merchant">{transaction.card_acceptor_details?.name || '—'}</SummaryItem>
      <SummaryItem label="Date">{formatDateTime(transaction.created_at, { timeZoneFormat: 'local' })}</SummaryItem>
      <SummaryItem label="Cardholder settled amount">{formatNumber(settled.amount, settled.currency_code)}</SummaryItem>
      <SummaryItem label="Billing currency">{settled.currency_code}</SummaryItem>
    </div>
  );

  return (
    <Inner px="var(--page-margin)" pb={16} data-testid={`new-dispute-${step}-step`}>
      {step === 'transaction' && (
        <div className="mx-auto flex max-w-xl flex-col gap-4 py-8">
          {transactionSummary}
          <ReasonField
            label="Card transaction ID"
            htmlFor="new-dispute-transaction-id"
            required
            description="Paste the settled card transaction ID to look it up automatically."
          >
            <input
              id="new-dispute-transaction-id"
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
              value={transactionIdInput}
              placeholder="ctxn_..."
              data-dd-privacy="hidden"
              data-testid="new-dispute-transaction-id"
              onChange={(event) => handleTransactionIdChange(event.target.value)}
            />
          </ReasonField>

          {activeDispute && (
            <Alert data-testid="new-dispute-existing-alert">
              <AlertTitle>This transaction already has an active dispute</AlertTitle>
              <AlertDescription>
                <a
                  className="text-primary underline"
                  href={`${ROUTE.CARD_DISPUTES}/edit/${activeDispute.id}`}
                  data-testid="new-dispute-existing-link"
                >
                  Open the existing dispute
                </a>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleTransactionConfirmed}
              variant={canContinueWithTransaction ? 'primary' : 'secondary'}
              isDisabled={!canContinueWithTransaction}
              isLoading={transactionQuery.isFetching}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step !== 'transaction' && !transaction && (
        <Loading isLoading={transactionQuery.isLoading} fullHeight>
          <div className="py-8" />
        </Loading>
      )}

      {step !== 'transaction' && transaction && settled && currency && (
        <div className="flex flex-col gap-6 py-6">
          {transactionSummary}

          {activeDispute && (
            <Alert data-testid="new-dispute-existing-alert">
              <AlertTitle>This transaction already has an active dispute</AlertTitle>
              <AlertDescription>
                <a className="text-primary underline" href={`${ROUTE.CARD_DISPUTES}/edit/${activeDispute.id}`}>
                  Open the existing dispute
                </a>
              </AlertDescription>
            </Alert>
          )}

          {step === 'claim' && (
            <div className="flex max-w-2xl flex-col gap-6">
              <ReasonField label="Reason" required description="The reason is permanent once the dispute is created.">
                <Select
                  value={reason}
                  onValueChange={(next) => {
                    // Changing the reason never carries another reason's answers forward.
                    setReason(next as CardDisputeReason);
                    setReasonState(emptyReasonFormState());
                    eligibility.reset();
                    setShowValidation(false);
                  }}
                >
                  <SelectTrigger className="w-full" data-testid="new-dispute-reason" aria-label="Dispute reason">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent position="popper" align="end" data-dd-privacy="hidden">
                    {REASON_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </ReasonField>

              <ReasonField
                label={`Claim amount (${currency})`}
                required
                description={`In the cardholder's billing currency. Up to ${formatNumber(settledSmallest ?? 0, currency)}.`}
              >
                <input
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  value={claimAmount}
                  inputMode="decimal"
                  data-dd-privacy="hidden"
                  data-testid="new-dispute-claim-amount"
                  onChange={(event) => setClaimAmount(event.target.value)}
                />
                {showValidation && claimAmount !== '' && claimSmallest === undefined && (
                  <p className="text-xs text-destructive" data-testid="new-dispute-claim-amount-error">
                    Enter a valid amount in {currency}.
                  </p>
                )}
                {claimOverReceiptDifference && (
                  <p className="text-xs text-destructive" data-testid="new-dispute-claim-amount-error">
                    For incorrect-amount disputes the claim may not exceed the settled amount minus the receipt amount.
                  </p>
                )}
              </ReasonField>

              {claimPartial && (
                <ReasonField
                  label="Why is the claim less than the full transaction amount?"
                  required
                  description={'Required when disputing a partial amount. Up to 10,000 characters.'}
                >
                  <Textarea
                    value={partialExplanation}
                    disabled={false}
                    data-dd-privacy="hidden"
                    data-testid="new-dispute-partial-explanation"
                    onChange={(event) => setPartialExplanation(event.target.value)}
                  />
                  {reasonForPartialDisputeTooLong(partialExplanation) && (
                    <p className="text-xs text-destructive">The explanation is limited to 10,000 characters.</p>
                  )}
                </ReasonField>
              )}

              {reason && (
                <DisputeReasonForm
                  reason={reason}
                  value={reasonState}
                  onChange={setReasonState}
                  currency={currency}
                  missingFields={showValidation ? missingFields : []}
                  oversizedField={showValidation ? oversizedField : undefined}
                />
              )}

              {eligibility.error && (
                <Alert variant="destructive">
                  <AlertTitle>Eligibility check failed</AlertTitle>
                  <AlertDescription>{eligibility.error}</AlertDescription>
                </Alert>
              )}

              {eligibility.eligibility && !eligibility.eligibility.eligible && (
                <Alert
                  variant={
                    eligibility.eligibility.reasons.some(
                      (entry) => entry !== CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE
                    )
                      ? 'destructive'
                      : 'default'
                  }
                  data-testid="new-dispute-eligibility-result"
                >
                  {eligibility.eligibility.reasons.some(
                    (entry) => entry !== CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE
                  ) ? (
                    <>
                      <AlertTitle>This transaction cannot be disputed right now</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc pl-4">
                          {eligibility.eligibility.reasons
                            .filter((entry) => entry !== CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE)
                            .map((entry) => (
                              <li key={entry}>{eligibilityReasonLabel(entry)}</li>
                            ))}
                        </ul>
                        {eligibility.eligibility?.existing_card_dispute_id && (
                          <a
                            className="text-primary underline"
                            href={`${ROUTE.CARD_DISPUTES}/edit/${eligibility.eligibility.existing_card_dispute_id}`}
                            data-testid="new-dispute-existing-link"
                          >
                            Open the existing dispute
                          </a>
                        )}
                      </AlertDescription>
                    </>
                  ) : (
                    <>
                      <AlertTitle>Required evidence is not complete yet</AlertTitle>
                      <AlertDescription>
                        The written statement and documents are collected in the next step. Saving a draft does not
                        require them; submitting does.
                      </AlertDescription>
                    </>
                  )}
                </Alert>
              )}

              <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setStep('transaction')}>
                  Back
                </Button>
                <Button
                  onClick={() => void handleClaimContinue()}
                  isDisabled={
                    !reason ||
                    claimSmallest === undefined ||
                    claimSmallest <= 0 ||
                    claimOverReceiptDifference ||
                    Boolean(oversizedField) ||
                    missingFields.length > 0 ||
                    eligibility.isChecking
                  }
                  isLoading={eligibility.isChecking}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 'evidence' && reason && (
            <div className="flex max-w-2xl flex-col gap-6">
              <div>
                <Label className="text-sm font-medium text-foreground" htmlFor="new-dispute-narrative">
                  Written statement
                </Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Describe the problem from the cardholder's perspective. Most reasons require a statement before
                  submission.
                </p>
                <Textarea
                  id="new-dispute-narrative"
                  className="mt-2"
                  value={narrative}
                  disabled={uploadBusy}
                  data-dd-privacy="hidden"
                  data-testid="new-dispute-narrative"
                  placeholder="Explain what happened"
                  onChange={(event) => setNarrative(event.target.value)}
                />
              </div>

              <DisputeEvidenceComposer value={evidence} onChange={setEvidence} onBusyChange={setUploadBusy} />

              <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setStep('claim')}>
                  Back
                </Button>
                <Button onClick={handleReviewContinue} isDisabled={uploadBusy}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 'review' && reason && claimSmallest !== undefined && (
            <ReviewStep
              currency={currency}
              reason={reason}
              claimAmount={claimSmallest}
              claimPartial={claimPartial}
              partialExplanation={partialExplanation}
              narrative={narrative}
              evidence={evidence}
              reasonState={reasonState}
              settledSmallest={settledSmallest ?? 0}
              wantsCredit={wantsCredit}
              creditConfirmed={creditConfirmed}
              onWantsCredit={setWantsCredit}
              onCreditConfirmed={setCreditConfirmed}
              onBack={() => setStep('evidence')}
              onSaveDraft={() => void handleSaveDraft()}
              saving={createMutation.isPending}
              createError={createError}
              readiness={disputeSubmissionReadiness({
                reason,
                formState: reasonState,
                currency,
                narrative,
                evidence,
                requiresPartialExplanation: claimPartial,
                partialExplanation,
              })}
            />
          )}
        </div>
      )}
    </Inner>
  );
};

const eligibilityReasonLabel = (reason: CardDisputeEligibilityReason): string =>
  (CARD_DISPUTE_ELIGIBILITY_REASON_LABELS as Record<string, string>)[reason] ?? reason;

interface ReviewStepProps {
  currency: string;
  reason: CardDisputeReason;
  claimAmount: number;
  claimPartial: boolean;
  partialExplanation: string;
  narrative: string;
  evidence: CardDisputeDocumentEvidenceInput[];
  reasonState: ReturnType<typeof emptyReasonFormState>;
  settledSmallest: number;
  wantsCredit: boolean;
  creditConfirmed: boolean;
  onWantsCredit: (value: boolean) => void;
  onCreditConfirmed: (value: boolean) => void;
  onBack: () => void;
  onSaveDraft: () => void;
  saving: boolean;
  createError?: string;
  readiness: { ready: boolean; missing: string[] };
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  currency,
  reason,
  claimAmount,
  claimPartial,
  partialExplanation,
  narrative,
  evidence,
  reasonState,
  settledSmallest,
  wantsCredit,
  creditConfirmed,
  onWantsCredit,
  onCreditConfirmed,
  onBack,
  onSaveDraft,
  saving,
  createError,
  readiness,
}) => {
  const answers = questionnaireAnswers(reason, reasonState, currency, ANSWER_LABELS);

  return (
    <div className="flex max-w-2xl flex-col gap-6" data-testid="new-dispute-review">
      <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-2">
        <SummaryItem label="Reason">{CARD_DISPUTE_REASON_LABELS[reason]}</SummaryItem>
        <SummaryItem label="Workflow">{workflowLabels(REASON_WORKFLOW[reason])}</SummaryItem>
        <SummaryItem label="Claim amount">{formatNumber(claimAmount, currency)}</SummaryItem>
        <SummaryItem label="Settled transaction amount">{formatNumber(settledSmallest, currency)}</SummaryItem>
        <SummaryItem label="Billing currency">{currency}</SummaryItem>
        {answers.map((entry) => (
          <SummaryItem key={entry.label} label={entry.label}>
            {entry.answer}
          </SummaryItem>
        ))}
        <SummaryItem label="Written statement">{narrative.trim() ? narrative.trim() : '—'}</SummaryItem>
        <SummaryItem label="Documents">{evidence.length > 0 ? `${evidence.length} document(s)` : 'None'}</SummaryItem>
        {claimPartial && <SummaryItem label="Partial explanation">{partialExplanation.trim() || '—'}</SummaryItem>}
      </div>

      {!readiness.ready && (
        <Alert>
          <AlertTitle>A draft can be saved now; submitting needs more information</AlertTitle>
          <AlertDescription>
            Before submitting to the Card Network you will still need {readiness.missing.join(', ')}. You can complete
            these on the dispute after saving the draft.
          </AlertDescription>
        </Alert>
      )}

      <div
        className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
        data-testid="new-dispute-credit-section"
      >
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground">
          <Checkbox
            checked={wantsCredit}
            onCheckedChange={(checked) => onWantsCredit(checked === true)}
            data-testid="new-dispute-credit-toggle"
          />
          Issue provisional credit
        </label>
        {wantsCredit && (
          <label
            className="flex cursor-pointer items-start gap-2 rounded-md bg-muted/50 p-3 text-sm text-foreground"
            data-testid="new-dispute-credit-confirmation"
          >
            <Checkbox checked={creditConfirmed} onCheckedChange={(checked) => onCreditConfirmed(checked === true)} />
            <span>
              I understand the full dispute amount ({formatNumber(claimAmount, currency)}) will be credited to the
              cardholder immediately from the program operating account — including while this is only a saved draft.
              Credit does not wait for the Card Network to accept the dispute.
            </span>
          </label>
        )}
      </div>

      {createError && (
        <Alert variant="destructive" data-testid="new-dispute-create-error">
          <AlertTitle>The dispute was not saved</AlertTitle>
          <AlertDescription>{createError}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onSaveDraft} isDisabled={saving || (wantsCredit && !creditConfirmed)} isLoading={saving}>
          Save draft
        </Button>
      </div>
    </div>
  );
};

const SummaryItem = ({ label, children }: React.PropsWithChildren<{ label: string }>) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <span className="whitespace-pre-wrap break-words text-sm font-medium text-foreground">{children}</span>
  </div>
);
